import { sourceComplete } from "./sourceAnswers.ts";
import type { CapacityCalculation, CapacityWorkspace, DirectLoadedVatCandidate } from "../api";
import type { WorkflowQuestion } from "./GuidedQuestion";

export type LoadingAction = "accounting" | "period" | "stock" | "recipe";

export function provisionalCalculation(result: CapacityCalculation | null) {
  if (result?.status !== "limited" || result.summary.provisional !== true) return null;
  return {
    title: "Doğrulanmış kayıtlarla geçici hesap",
    amountLabel: "Geçici hesaplanan iade",
    unresolvedCount: result.summary.unresolved_count || 0,
    unresolvedVat: result.summary.unresolved_vat || "0",
  };
}

export function pendingPurchaseAnswers(questions: WorkflowQuestion[], drafts: Record<string, string>): Record<string, string> {
  return Object.fromEntries(questions.filter(question => !question.applied).flatMap(question => question.members || [question])
    .filter(question => {
      const answer = drafts[question.id];
      if (question.applied || !answer || answer === question.answer) return false;
      if (question.input_kind) return sourceComplete(question, answer);
      if (question.kind === "capacity_conversion" && ["usage:general_expense", "usage:fixed_asset"].includes(answer)) return true;
      if (question.kind === "capacity_conversion" || question.kind === "loading") {
        return question.options.some(option => !option.disabled
          && (option.value === answer && answer !== "custom"
            || option.value === "custom" && answer.startsWith("custom:")));
      }
      if (question.kind === "recipe_source") {
        if (answer.startsWith("recipe:") || ["trade", "exclude", "defer"].includes(answer)) {
          return question.options.some(option => option.value === answer && !option.disabled);
        }
        return answer.startsWith("custom:") && Boolean(question.selection_options?.some(option => option.value === answer.slice(7)))
          && !question.options.some(option => option.value === `recipe:${answer.slice(7)}` && option.disabled);
      }
      if (question.kind === "capacity_material") return (!question.loading_review || question.options.some(option => option.value === "exclude" || option.value === "usage:general_expense" || option.value.startsWith("capacity-material:")))
        && (answer.startsWith("capacity-material:") || ["exclude", "defer", "usage:general_expense"].includes(answer))
        && question.options.some(option => option.value === answer && !option.disabled);
      if (!answer.startsWith("custom:")) return question.options.some(option => !option.disabled && option.value === answer && answer !== "custom");
      if (!question.options.some(option => !option.disabled && option.value === "custom")) return false;
      if (question.kind === "sector") return Boolean(question.selection_options?.some(option => option.value === answer.slice(7)));
      if (question.kind === "return_reference") return answer.slice(7).split(",").every(value =>
        Boolean(value.trim()) && question.selection_options?.some(option => option.value === value.trim()));
      try {
        const mapping = JSON.parse(answer.slice(7));
        if (!mapping || Array.isArray(mapping) || typeof mapping !== "object" || !Object.keys(mapping).length) return false;
        if (question.kind === "unit_group") return question.units?.every(row =>
          ["C62", "KGM", "GRM", "LTR", "MTR", "defer"].includes(mapping[row.key])) === true
          && Object.keys(mapping).length === question.units.length;
        if (question.kind === "material") return Array.isArray(mapping.candidate_ids) && mapping.candidate_ids.length > 0
          && mapping.candidate_ids.every((id: number) => question.selection_options?.some(option => option.value === String(id)));
        return question.facts?.rows.every(row => Array.isArray(mapping[row.key])
          && mapping[row.key].length === question.facts!.columns.length
          && mapping[row.key].every((cell: unknown) => typeof cell === "string" && cell.trim().length > 0)) === true
          && Object.keys(mapping).length === question.facts.rows.length;
      } catch { return false; }
    })
    .map(question => [question.id, drafts[question.id]]));
}

export function getLoadingAction(question: WorkflowQuestion): LoadingAction {
  if (question.options.some(option => option.value === "updated")) return "accounting";
  if (question.loading_periods) return "period";
  return question.title.startsWith("Ticari mal") ? "stock" : "recipe";
}

export function hasActionableStockInformation(question: WorkflowQuestion, rows: DirectLoadedVatCandidate[]): boolean {
  if (question.kind !== "loading" || getLoadingAction(question) !== "stock") return true;
  const ids = new Set(question.invoice_line_ids || []);
  const selected = rows.filter(row => ids.has(row.invoice_line_id));
  // No matching sale is not evidence of missing information: the purchase may
  // remain in stock. Keep actual source errors and incomplete analyses visible.
  return selected.length !== ids.size || selected.some(row => row.issue_codes.length > 0
    || !(Number(row.quantity) > 0) || !row.unit);
}

export function loadingReviewSummary(question: WorkflowQuestion, rows: DirectLoadedVatCandidate[]) {
  const ids = new Set(question.invoice_line_ids || []);
  const selected = rows.filter(row => ids.has(row.invoice_line_id));
  const reasons = new Map<string, { code: string; label: string; count: number }>();
  for (const row of selected) {
    const code = row.recipe_check?.code || "unknown";
    const reason = reasons.get(code) || { code, label: row.recipe_check?.label || "Satırın kaynak bilgisi kontrol edilmeli", count: 0 };
    reason.count++;
    reasons.set(code, reason);
  }
  return {
    reasons: [...reasons.values()],
    consumptionExplained: ids.size > 0 && selected.length === ids.size
      && selected.every(row => ["fifo", "other_sales"].includes(row.recipe_check?.code || "")),
  };
}

// Follow the existing material match; never infer a recipe from similar purchase names.
export function relatedLoadingRecipes(rows: DirectLoadedVatCandidate[], recipes: CapacityWorkspace["capacity_recipes"]) {
  const keys = new Set(recipes.filter(component => rows.some(row =>
    row.recipe_check ? row.recipe_check.editable_recipe_keys.includes(component.recipe_key) : row.matched_capacity_code
      ? row.matched_capacity_code === component.material_code
      : Boolean(row.matched_capacity_input) && row.matched_capacity_input === component.material_name
  )).map(component => component.recipe_key));
  return recipes.filter(component => keys.has(component.recipe_key));
}

export function missingLoadingRecipes(rows: DirectLoadedVatCandidate[], recipes: CapacityWorkspace["capacity_recipes"]) {
  return relatedLoadingRecipes(rows, recipes).filter(component =>
    !Number(component.material_quantity) || Number(component.material_quantity) < 0 || !component.material_unit);
}

export function filterLoadingRecipeRows(rows: DirectLoadedVatCandidate[], reason: string, search: string) {
  const query = search.trim().toLocaleLowerCase("tr");
  return rows.filter(row => (!reason || row.recipe_check?.code === reason) &&
    (!query || `${row.invoice_no} ${row.item_name} ${row.matched_capacity_input}`.toLocaleLowerCase("tr").includes(query)));
}
